"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import SplitText from "@/components/bits/SplitText/SplitText";
import Link from "next/link";
import { retrieveTopCategoriesWithEpisodes } from "@/app/actions/category/retrieve";
import EpisodeCard from "@/components/episode-card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import {
  ChevronRight,
  Loader2,
  AlertCircle,
  Brain,
  Rocket,
  Laptop,
  Banknote,
  FlaskConical,
  Building2,
  Cloud,
  Cpu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CategoryWithEpisodes } from "@/app/actions/category/retrieve";
import { Session } from "@/lib/user";
import type { Variants } from "framer-motion";
import CategoryFollowButton from "@/components/category/category-follow-button";
import { notoSerif } from "@/lib/fonts";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring" as const,
      stiffness: 100,
      damping: 12,
    },
  },
};

const badgeVariants: Variants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: (i: number) => ({
    scale: 1,
    opacity: 1,
    transition: {
      delay: i * 0.05,
      duration: 0.3,
      type: "spring" as const,
      stiffness: 100,
    },
  }),
  tap: {
    scale: 0.97,
    transition: {
      duration: 0.2,
    },
  },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.1,
      duration: 0.4,
      ease: "easeOut" as const,
    },
  }),
};

// Core categories — tech-focused buckets
const coreCategories = [
  {
    id: "artificial-intelligence",
    name: "AI & ML",
    icon: <Brain className="h-4 w-4" />,
  },
  {
    id: "startups",
    name: "Startups",
    icon: <Rocket className="h-4 w-4" />,
  },
  {
    id: "venture-capital",
    name: "Venture Capital",
    icon: <Banknote className="h-4 w-4" />,
  },
  {
    id: "developer-tools",
    name: "Developer Tools",
    icon: <Laptop className="h-4 w-4" />,
  },
  {
    id: "big-tech",
    name: "Big Tech",
    icon: <Building2 className="h-4 w-4" />,
  },
  {
    id: "cloud-infrastructure",
    name: "Cloud & Infra",
    icon: <Cloud className="h-4 w-4" />,
  },
  {
    id: "technology",
    name: "Technology",
    icon: <Cpu className="h-4 w-4" />,
  },
  {
    id: "science",
    name: "Science",
    icon: <FlaskConical className="h-4 w-4" />,
  },
];

interface BrowseCategoriesProps {
  session?: Session | null;
}

export default function BrowseCategories({ session }: BrowseCategoriesProps) {
  const [categories, setCategories] = useState<CategoryWithEpisodes[]>([]);
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Initialize fetch function with useCallback to prevent unnecessary re-creations
  const fetchMoreCategories = useCallback(async () => {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;

    try {
      setIsLoading(true);
      setError(null);
      // Fetch 5 categories per page with 3 episodes each
      const newCategories = await retrieveTopCategoriesWithEpisodes(
        5,
        3,
        page * 5
      );

      if (newCategories.length === 0) {
        setHasMore(false);
      } else {
        setCategories((prev) => [...prev, ...newCategories]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Failed to load categories. Please try again.");
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [page, hasMore]);

  // Initial load
  useEffect(() => {
    fetchMoreCategories();
  }, [fetchMoreCategories]);

  // Set up intersection observer
  useEffect(() => {
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Only create observer if we have more content to load
    if (!hasMore) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          !isFetchingRef.current
        ) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px", // Load earlier before user reaches the bottom
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observerRef.current.observe(currentTarget);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoading]);

  // Effect to fetch more when page changes
  useEffect(() => {
    if (page > 0) {
      fetchMoreCategories();
    }
  }, [page, fetchMoreCategories]);

  // Handle retry on error
  const handleRetry = () => {
    setError(null);
    fetchMoreCategories();
  };

  return (
    <motion.div
      className="mt-10 space-y-16"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Core categories badges section */}
      <motion.section variants={itemVariants}>
        <h3 className={`text-lg font-medium ${notoSerif.className} mb-4`}>
          Popular Categories
        </h3>
        <div className="flex flex-wrap gap-2">
          {coreCategories.map((category, index) => (
            <Link
              key={category.id}
              href={`/category/${category.id}`}
              className="no-underline select-none"
              draggable={false}
            >
              <motion.div
                custom={index}
                variants={badgeVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                whileTap="tap"
              >
                <Badge
                  variant="outline"
                  className="px-3 py-1.5 flex items-center gap-1.5 transition-all hover:bg-primary hover:text-primary-foreground cursor-pointer"
                >
                  {category.icon}
                  {category.name}
                </Badge>
              </motion.div>
            </Link>
          ))}
        </div>
      </motion.section>

      {categories.length === 0 && isLoading ? (
        Array.from({ length: 2 }).map((_, idx) => (
          <motion.section
            key={`initial-skeleton-${idx}`}
            className="space-y-6"
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: idx * 0.2 }}
          >
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-full max-w-xl mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <motion.div
                  key={`initial-skeleton-item-${idx}-${index}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <Skeleton className="h-[24.5rem] w-full" />
                </motion.div>
              ))}
            </div>
          </motion.section>
        ))
      ) : categories.length === 0 && error ? (
        <motion.div
          className="text-center py-10"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            initial={{ rotate: 0 }}
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
          </motion.div>
          <motion.p
            className="text-muted-foreground mb-4"
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {error}
          </motion.p>
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button onClick={handleRetry} variant="outline">
              Try Again
            </Button>
          </motion.div>
        </motion.div>
      ) : categories.length === 0 ? (
        <motion.div className="text-center" variants={itemVariants}>
          <p className="text-muted-foreground">No categories available yet.</p>
        </motion.div>
      ) : (
        categories.map((category, categoryIndex) => (
          <motion.section
            key={`${category.category.id}-${categoryIndex}`}
            className="space-y-6"
            variants={itemVariants}
            custom={categoryIndex}
            initial="hidden"
            animate="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <Link
                  href={`/category/${category.category.slug}`}
                  className="group inline-flex items-center w-max"
                >
                  <motion.h2
                    className={`text-xl md:text-2xl font-semibold ${notoSerif.className} group-hover:underline group-hover:decoration-2 group-hover:decoration-primary/50 transition-underline`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * categoryIndex, duration: 0.4 }}
                  >
                    {category.category.name}
                  </motion.h2>
                  <ChevronRight className="ml-1 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
                {category.category.description && (
                  <motion.p
                    className="text-sm text-muted-foreground max-w-2xl"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 * categoryIndex + 0.2 }}
                  >
                    {category.category.description}
                  </motion.p>
                )}
              </div>

              <motion.div
                className="hidden md:flex items-center gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 * categoryIndex + 0.3 }}
              >
                <CategoryFollowButton
                  categoryId={category.category.id}
                  categoryName={category.category.name}
                  userId={session?.user?.id}
                  size="sm"
                  variant="ghost"
                />
                <Link href={`/category/${category.category.slug}`}>
                  <Button variant="secondary" size="sm" className="gap-1.5">
                    View All
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </Link>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {category.episodes.length > 0
                ? category.episodes.map((episode, index) => (
                    <motion.div
                      key={`${category.category.id}-${episode.id}-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: 0.1 * categoryIndex + 0.15 * index,
                        duration: 0.4,
                      }}
                    >
                      <EpisodeCard
                        episode={{
                          id: episode.id,
                          title: episode.title,
                          description: episode.description,
                          cover: episode.cover!,
                          user: {
                            name: episode.user?.name,
                            image: episode.user?.image,
                          },
                          views: episode.views,
                          date: episode.date,
                        }}
                      />
                    </motion.div>
                  ))
                : Array.from({ length: 3 }).map((_, index) => (
                    <motion.div
                      key={`empty-skeleton-${category.category.id}-${index}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 * index }}
                    >
                      <Skeleton className="h-[24.5rem] w-full" />
                    </motion.div>
                  ))}
            </div>

            <div className="md:hidden flex items-center justify-between mt-2">
              <CategoryFollowButton
                categoryId={category.category.id}
                categoryName={category.category.name}
                userId={session?.user?.id}
                size="sm"
                variant="ghost"
              />
              <Link href={`/category/${category.category.slug}`}>
                <Button variant="outline" size="sm" className="gap-1.5">
                  View All
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </Link>
            </div>
          </motion.section>
        ))
      )}

      {/* Intersection observer target */}
      <div ref={observerTarget} className="h-10 w-full">
        {isLoading && hasMore && (
          <motion.div
            className="flex justify-center items-center py-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </motion.div>
        )}

        {!isLoading && error && (
          <motion.div
            className="text-center py-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-muted-foreground mb-2">{error}</p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button onClick={handleRetry} size="sm" variant="outline">
                Retry
              </Button>
            </motion.div>
          </motion.div>
        )}

        {!isLoading && !hasMore && categories.length > 0 && (
          <motion.div
            className="text-center py-8 text-muted-foreground text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            You've reached the end of the content
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
